import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrEditCourse } from "../../utils/wellspoken.api";
import toast from "react-hot-toast";


export default function CreateCourse() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");



    const [formData, setFormData] = useState({
        courseId: 0,
        courseName: "",
        courseDescription: "",
        courseDuration: "",
        price: "",
        courseLevel: "",
        courseMode: "",
        startDate: "",
        endDate: "",
        items: [""],
        banner: "",
    });

    /* ---------------- Handlers ---------------- */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, value) => {
        const updated = [...formData.items];
        updated[index] = value;
        setFormData((prev) => ({ ...prev, items: updated }));
    };

    const addItem = () => {
        setFormData((prev) => ({ ...prev, items: [...prev.items, ""] }));
    };

    const removeItem = (index) => {
        setFormData((prev) => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index),
        }));
    };

    const handleBannerUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData((prev) => ({ ...prev, banner: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    /* ---------------- Submit ---------------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        const cleanedDescription = formData.courseDescription
            .split("\n")
            .map(line => line.trim())
            .filter(Boolean)
            .join("\n"); // or join(", ") if backend prefers

        try {
            // ✅ Create FormData
            const formDataToSend = new FormData();
            formDataToSend.append('CourseId', formData.courseId);
            formDataToSend.append('CourseName', formData.courseName);
            formDataToSend.append('CourseDescription', cleanedDescription);
            formDataToSend.append('CourseDuration', formData.courseDuration);
            formDataToSend.append('Price', formData.price);
            formDataToSend.append('CourseLevel', formData.courseLevel);
            formDataToSend.append('CourseMode', formData.courseMode);
            formDataToSend.append('StartDate', formData.startDate);
            formDataToSend.append('EndDate', formData.endDate);

            // Filter empty items
            formData.items.filter(Boolean).forEach((item, index) => {
                formDataToSend.append('Items', item);
            });

            // Append banner file (not base64)
            if (formData.bannerFile) {  // You'll need to store File reference
                formDataToSend.append('Banner', formData.bannerFile);
            }

            formDataToSend.append('IsDelete', 'false');

            await createOrEditCourse(formDataToSend);

            toast.success("Course created successfully 🎉");

            // ✅ OPTIONAL: reset form
            setFormData({
                courseId: 0,
                courseName: "",
                courseDescription: "",
                courseDuration: "",
                price: "",
                courseLevel: "",
                courseMode: "",
                startDate: "",
                endDate: "",
                items: [""],
                bannerFile: null,
            });

        } catch {
            setError("Failed to create course. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    return (
        <main className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <section className="w-full px-4 sm:px-6 lg:px-10">
                {/* Header */}
                <header className="mb-10 text-center">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                        Create New Course
                    </h1>
                    <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
                        Fill in the details below to publish a new course.
                    </p>
                </header>

                {/* Form */}
                <form
                    onSubmit={handleSubmit}
                    aria-label="Create course form"
                    className="
        w-full
        bg-white
        border border-gray-200
        rounded-2xl
        shadow-md
        p-6 sm:p-10
        space-y-8
      "
                >
                    {/* Course Name */}
                    <div>
                        <label
                            htmlFor="courseName"
                            className="block text-sm font-medium text-gray-900"
                        >
                            Course Name
                        </label>
                        <input
                            id="courseName"
                            name="courseName"
                            value={formData.courseName}
                            onChange={handleChange}
                            required
                            aria-required="true"
                            className="
            mt-2 w-full rounded-lg
            border border-border
            px-4 py-3
            focus:outline-none
            focus-visible:ring-2
            focus-visible:ring-primary
          "
           placeholder="Course Name"
                        />
                    </div>

                    {/* Course Level */}
                    <div>
                        <label
                            htmlFor="courseLevel"
                            className="block text-sm font-medium text-gray-900"
                        >
                            Course Level
                        </label>
                        <input
                            id="courseLevel"
                            name="courseLevel"
                            value={formData.courseLevel}
                            onChange={handleChange}
                            required
                            className="
            mt-2 w-full rounded-lg
            border border-border
            px-4 py-3
            focus:outline-none
            focus-visible:ring-2
            focus-visible:ring-primary
          "
          placeholder="Course Level"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label
                            htmlFor="courseDescription"
                            className="block text-sm font-medium text-gray-900 mb-2"
                        >
                            Course Highlights
                        </label>
                        <textarea
                            id="courseDescription"
                            name="courseDescription"
                            value={formData.courseDescription}
                            onChange={handleChange}
                            rows={4}
                            required
                            placeholder={`Everyday conversations & small talk
Pronunciation practice
Role-plays for real-life situations`}
                            className="
            mt-2 w-full rounded-lg
            border border-border
            px-4 py-3
            focus:outline-none
            focus-visible:ring-2
            focus-visible:ring-primary
          "
                        />
                    </div>

                    {/* Grid Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <InputField
                            label="Duration (weeks)"
                            name="courseDuration"
                            type="number"
                            value={formData.courseDuration}
                            onChange={handleChange}
                        />
                        <InputField
                            label="Price (₹)"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={handleChange}
                        />
                        <SelectField
                            label="Mode"
                            name="courseMode"
                            value={formData.courseMode}
                            onChange={handleChange}
                            options={["Online", "Offline"]}
                        />
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <InputField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            value={formData.startDate}
                            onChange={handleChange}
                        />
                        <InputField
                            label="End Date"
                            name="endDate"
                            type="date"
                            value={formData.endDate}
                            onChange={handleChange}
                        />
                    </div>

                    {/* Error */}
                    {error && (
                        <p className="text-sm text-pink-600" role="alert">
                            {error}
                        </p>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="
            px-6 py-3 rounded-full
            border border-gray-300
            text-gray-700
            hover:bg-gray-50
            focus-visible:ring-2 focus-visible:ring-indigo-500
          "
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="
            px-8 py-3 rounded-full
            bg-gradient-to-r from-blue-500 to-indigo-600
            text-white font-semibold
            hover:opacity-90
            focus-visible:ring-2 focus-visible:ring-blue-500
            disabled:opacity-60
          "
                        >
                            {loading ? "Saving..." : "Create Course"}
                        </button>
                    </div>
                </form>
            </section>
        </main>

    );
}

/* ---------- Small Reusable Inputs ---------- */

function InputField({ label, ...props }) {
    return (
        <div>
            <label className="block text-sm font-medium text-textPrimary">
                {label}
            </label>
            <input
                {...props}
                required
                className="mt-1 w-full rounded-lg border border-border px-4 py-2.5
        focus-visible:ring-2 focus-visible:ring-primary outline-none"
            />
        </div>
    );
}

function SelectField({ label, options, ...props }) {
    return (
        <div>
            <label className="block text-sm font-medium text-textPrimary">
                {label}
            </label>
            <select
                {...props}
                required
                className="mt-1 w-full rounded-lg border border-border px-4 py-2.5
        focus-visible:ring-2 focus-visible:ring-primary outline-none"
            >
                <option value="">Select</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>
                        {opt}
                    </option>
                ))}
            </select>
        </div>
    );
}
